import { AxiosPromise, AxiosResponse } from 'axios';

import { request } from '@cvt/clients/baseClient';
import { camelizeObject, snakizeObject } from '@cvt/helpers/object';
import { DEFAULT_QUERY_LIMIT } from '@cvt/helpers/query';

import { mapUserCrudData, mapUserCrudFormData, mapUserData } from './userClient.formatter';


const usersApiBaseUrl = import.meta.env.VITE__CVT_API_URL;

const getUser = (params: { id: number | string }): AxiosPromise<Users.User> => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/users/${params.id}/`,
      method: 'GET',
    },
  }).then((data: AxiosResponse<Users.UserApi>) => ({
    ...data,
    data: mapUserData(data.data),
  }));
};

const getUsers = (params: Users.GetListParams): AxiosPromise<CVT.Query.PaginatedResults<Users.User>> => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/users/`,
      method: 'GET',
      params: snakizeObject(params),
    },
  }).then((data: AxiosResponse<CVT.Query.PaginatedResults<Users.UserApi>>) => ({
    ...data,
    data: {
      ...data.data,
      results: data.data.results.map(mapUserData),
    },
  }));
};

const createUser = (data: Users.Create): AxiosPromise<Users.User> => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/users/`,
      method: 'POST',
      data: mapUserCrudData(data),
    },
  }).then((data: AxiosResponse<Users.UserApi>) => ({
    ...data,
    data: mapUserData(data.data),
  }));
};

const editUser = (data: Users.Edit): AxiosPromise<Users.User> => {
  return request({
    options: {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      url: `${usersApiBaseUrl}/users/${data.id}/`,
      method: 'PUT',
      data: mapUserCrudFormData(data),
    },
  }).then((data: AxiosResponse<Users.UserApi>) => ({
    ...data,
    data: mapUserData(data.data),
  }));
};

const patchUser = (data: Partial<Users.Edit>): AxiosPromise<Users.User> => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/users/${data.id}/`,
      method: 'PATCH',
      data: mapUserCrudData(data),
    },
  }).then((data: AxiosResponse<Users.UserApi>) => ({
    ...data,
    data: mapUserData(data.data),
  }));
};

const deleteUser = (params: { id: number | string }): AxiosPromise<void> => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/users/${params.id}/`,
      method: 'DELETE',
    },
  });
};

const inviteUser = (data: { email: string }): AxiosPromise<void> => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/user-invitations/`,
      method: 'POST',
      data,
    },
  }).then((data: AxiosResponse) => ({
    ...data,
    data: camelizeObject(data.data),
  }));
};

const getInvite = (params: { id: string }): AxiosPromise<Auth.UserInvite> => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/user-invitations/${params.id}/`,
      method: 'GET',
    },
    authenticate: false,
  }).then((data: AxiosResponse<Auth.UserInviteApi>) => ({
    ...data,
    data: camelizeObject(data.data),
  }));
};

const getPreferenceHistory = (params: Users.GetPreferenceHistoryParams): AxiosPromise<CVT.Query.PaginatedResults<Users.PreferenceHistoryItem>>  => {
  return request({
    options: {
      url: `${usersApiBaseUrl}/users-preferences-history/`,
      method: 'GET',
      params: snakizeObject({
        ...params,
        limit: params.limit || DEFAULT_QUERY_LIMIT,
        offset: params.offset,
      }),
    },
  }).then((data: AxiosResponse<CVT.Query.PaginatedResults<Users.PreferenceHistoryItemApi>>) => ({
    ...data,
    data: {
      ...data.data,
      results: data.data.results.map((item) => camelizeObject<Users.PreferenceHistoryItem>(item)),
    },
  }));
};

export const userClient = {
  getUser,
  getUsers,
  createUser,
  editUser,
  patchUser,
  deleteUser,
  inviteUser,
  getInvite,
  getPreferenceHistory,
};
